/**
 * P8-P0 — Design System resolution engine.
 *
 * Single deterministic lookup with a guaranteed fallback:
 *
 *     resolveToken(key) = runtimeOverride[key]   // P8-P2 (CDN/runtime)  — stub
 *                      ?? adminOverride[key]      // P8-P1 (admin/DB)      — stub
 *                      ?? localDefault[key]       // P8-P0 (in-repo)       — always present
 *
 * In PR1 both override layers are inactive (null), so resolution always returns
 * the local default → behaviour-neutral. The override layers are wired in later
 * phases WITHOUT changing any call site.
 *
 * Invariant: resolution never throws. Unknown keys return `undefined` (callers
 * that need a hard fallback should pass one), never an exception.
 */
import DESIGN_TOKENS from './tokens';

// --- Override layers (filled in P8-P1 / P8-P2) ---
// adminOverrides is hydrated at runtime from system_settings.design_system.theme
// via `setAdminOverrides`. It stays null (=> local defaults) until an admin has
// BOTH enabled the Design System AND provided token deltas → behaviour-neutral.
let adminOverrides = null; // P8-P1: hydrated from system_settings.design_system.theme
const runtimeOverrides = null; // P8-P2: hydrated from versioned CDN/runtime config

/**
 * Hydrate (or clear) the admin override layer. Accepts a `tokens.js`-shaped
 * object (`{ font, radius, shadow, space, color, surface }`) or null/undefined
 * to deactivate. Passing a non-object clears the layer. Never throws.
 */
export function setAdminOverrides(overrides) {
  adminOverrides = overrides && typeof overrides === 'object' ? overrides : null;
}

function pick(obj, path) {
  if (!obj) return undefined;
  return path.split('.').reduce((acc, k) => (acc == null ? undefined : acc[k]), obj);
}

/**
 * Resolve a dot-path design token (e.g. 'font.sans', 'radius.base').
 * @param {string} key
 * @param {object} [ctx] reserved for future preset/direction-aware resolution
 */
export function resolveToken(key, _ctx = {}) {
  const r = pick(runtimeOverrides, key);
  if (r !== undefined && r !== null) return r;
  const a = pick(adminOverrides, key);
  if (a !== undefined && a !== null) return a;
  return pick(DESIGN_TOKENS, key);
}

/** Whether any override layer is active. Always false in PR1. */
export function hasOverrides() {
  return Boolean(adminOverrides || runtimeOverrides);
}

export default resolveToken;
